import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { AriaLiveProvider } from "@/components/AriaLiveRegion";
import CartSidebar from "@/components/CartSidebar";
import ScrollProgress from "@/components/ScrollProgress";
import SkipToContent from "@/components/SkipToContent";
import ChatAssistant from "@/components/ChatAssistant";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Shop = lazy(() => import("./pages/Shop"));
const Categories = lazy(() => import("./pages/Categories"));
const Category = lazy(() => import("./pages/Category"));
const Gifts = lazy(() => import("./pages/Gifts"));
const GiftCategory = lazy(() => import("./pages/GiftCategory"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Profile = lazy(() => import("./pages/Profile"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const UserOrders = lazy(() => import("./pages/Orders"));
const Story = lazy(() => import("./pages/Story"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ReturnsPolicy = lazy(() => import("./pages/ReturnsPolicy"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const ShippingInfo = lazy(() => import("./pages/ShippingInfo"));
const CmsPage = lazy(() => import("./pages/CmsPage"));
const GiftCardsPage = lazy(() => import("./pages/GiftCards"));
const Artisans = lazy(() => import("./pages/Artisans"));
const ArtisanDetails = lazy(() => import("./pages/ArtisanDetails"));
const Wholesale = lazy(() => import("./pages/Wholesale"));
const Loyalty = lazy(() => import("./pages/Loyalty"));
const Referral = lazy(() => import("./pages/Referral"));
const SharedWishlist = lazy(() => import("./pages/SharedWishlist"));
const Support = lazy(() => import("./pages/Support"));
const Donate = lazy(() => import("./pages/Donate"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Products = lazy(() => import("./pages/admin/Products"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Inventory = lazy(() => import("./pages/admin/Inventory"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const Brands = lazy(() => import("./pages/admin/Brands"));
const Discounts = lazy(() => import("./pages/admin/Discounts"));
const Banners = lazy(() => import("./pages/admin/Banners"));
const CmsPages = lazy(() => import("./pages/admin/CmsPages"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const AdminNewsletter = lazy(() => import("./pages/admin/Newsletter"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AdminTestimonials = lazy(() => import("./pages/admin/Testimonials"));
const SiteContent = lazy(() => import("./pages/admin/SiteContent"));
const EmailTemplate = lazy(() => import("./pages/admin/EmailTemplate"));
const AdminReturns = lazy(() => import("./pages/admin/Returns"));
const AdminGiftCards = lazy(() => import("./pages/admin/GiftCards"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminVariants = lazy(() => import("./pages/admin/Variants"));
const AdminArtisans = lazy(() => import("./pages/admin/Artisans"));
const AdminWholesale = lazy(() => import("./pages/admin/Wholesale"));
const AdminTickets = lazy(() => import("./pages/admin/Tickets"));
const AdminWhatsAppBroadcast = lazy(() => import("./pages/admin/WhatsAppBroadcast"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const AdminSegments = lazy(() => import("./pages/admin/Segments"));
const AdminMarketing = lazy(() => import("./pages/admin/Marketing"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <AriaLiveProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <SkipToContent />
                <ScrollProgress />
                <CartSidebar />
                <ChatAssistant />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/index.html" element={<Navigate to="/" replace />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/story" element={<Story />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/category/:slug" element={<Category />} />
                    <Route path="/gifts" element={<Gifts />} />
                    <Route path="/gifts/:slug" element={<GiftCategory />} />
                    <Route path="/product/:slug" element={<ProductDetails />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/sitemap" element={<Sitemap />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/returns" element={<ReturnsPolicy />} />
                    <Route path="/track" element={<TrackOrder />} />
                    <Route path="/shipping" element={<ShippingInfo />} />
                    <Route path="/wishlist" element={<Wishlist />} />
                    <Route path="/orders" element={<UserOrders />} />
                    <Route path="/gift-cards" element={<GiftCardsPage />} />
                    <Route path="/artisans" element={<Artisans />} />
                    <Route path="/artisan/:slug" element={<ArtisanDetails />} />
                    <Route path="/wholesale" element={<Wholesale />} />
                    <Route path="/loyalty" element={<Loyalty />} />
                    <Route path="/referral" element={<Referral />} />
                    <Route path="/wishlist/shared/:code" element={<SharedWishlist />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/donate" element={<Donate />} />
                    <Route path="/page/:slug" element={<CmsPage />} />
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<Dashboard />} />
                      <Route path="products" element={<Products />} />
                      <Route path="variants" element={<AdminVariants />} />
                      <Route path="orders" element={<Orders />} />
                      <Route path="returns" element={<AdminReturns />} />
                      <Route path="inventory" element={<Inventory />} />
                      <Route path="customers" element={<Customers />} />
                      <Route path="reviews" element={<AdminReviews />} />
                      <Route path="testimonials" element={<AdminTestimonials />} />
                      <Route path="newsletter" element={<AdminNewsletter />} />
                      <Route path="email-template" element={<EmailTemplate />} />
                      <Route path="categories" element={<AdminCategories />} />
                      <Route path="brands" element={<Brands />} />
                      <Route path="discounts" element={<Discounts />} />
                      <Route path="banners" element={<Banners />} />
                      <Route path="site-content" element={<SiteContent />} />
                      <Route path="pages" element={<CmsPages />} />
                      <Route path="gift-cards" element={<AdminGiftCards />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="artisans" element={<AdminArtisans />} />
                      <Route path="wholesale" element={<AdminWholesale />} />
                      <Route path="tickets" element={<AdminTickets />} />
                      <Route path="whatsapp" element={<AdminWhatsAppBroadcast />} />
                      <Route path="audit-logs" element={<AdminAuditLogs />} />
                      <Route path="segments" element={<AdminSegments />} />
                      <Route path="marketing" element={<AdminMarketing />} />
                      <Route path="settings" element={<Settings />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AriaLiveProvider>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;